import { languages } from "../../Data/languages";
import { currentLang, intlLocale, isTranslated, searchable } from "./index";

// Lists the checkout shows, in the page's language. Only the label changes:
// the value (what the order stores) is always the English one.

const LANGUAGE_NAMES_ES = {
  albanian: "Albanés",
  amharic: "Amárico",
  arabic: "Árabe",
  armenian: "Armenio",
  bengali: "Bengalí",
  bosnian: "Bosnio",
  bulgarian: "Búlgaro",
  burmese: "Birmano",
  catalan: "Catalán",
  chineseSimp: "Chino (simplificado)",
  chineseTrad: "Chino (tradicional)",
  creole: "Criollo haitiano",
  croatian: "Croata",
  czech: "Checo",
  danish: "Danés",
  dari: "Darí",
  dutch: "Neerlandés",
  english: "Inglés",
  estonian: "Estonio",
  farsi: "Farsi",
  finnish: "Finlandés",
  french: "Francés",
  frenchCan: "Francés (canadiense)",
  georgian: "Georgiano",
  german: "Alemán",
  greek: "Griego",
  gujrati: "Guyaratí",
  hausa: "Hausa",
  hebrew: "Hebreo",
  hindi: "Hindi",
  hmong: "Hmong",
  hungarian: "Húngaro",
  igbo: "Igbo",
  indonesian: "Indonesio",
  italian: "Italiano",
  japanese: "Japonés",
  kannada: "Canarés",
  khmer: "Jemer",
  korean: "Coreano",
  kurdish: "Kurdo",
  lao: "Lao",
  latin: "Latín",
  latvian: "Letón",
  lithuanian: "Lituano",
  macedonian: "Macedonio",
  malay: "Malayo",
  malayalam: "Malayalam",
  maltese: "Maltés",
  marathi: "Maratí",
  nepali: "Nepalí",
  norwegian: "Noruego",
  pashto: "Pastún",
  persian: "Persa",
  polish: "Polaco",
  portugueseBrazil: "Portugués (Brasil)",
  portuguesePortugal: "Portugués (Portugal)",
  punjabi: "Panyabí",
  romanian: "Rumano",
  russian: "Ruso",
  serbian: "Serbio",
  slovak: "Eslovaco",
  slovenian: "Esloveno",
  somali: "Somalí",
  spanish: "Español",
  swahili: "Suajili",
  swedish: "Sueco",
  tagalog: "Tagalo",
  tamil: "Tamil",
  telugu: "Telugu",
  thai: "Tailandés",
  tigrinya: "Tigriña",
  turkish: "Turco",
  ukrainian: "Ucraniano",
  urdu: "Urdu",
  uzbek: "Uzbeko",
  vietnamese: "Vietnamita",
  yoruba: "Yoruba",
};

const LANGUAGE_NAMES_PT = {
  albanian: "Albanês",
  amharic: "Amárico",
  arabic: "Árabe",
  armenian: "Armênio",
  bengali: "Bengali",
  bosnian: "Bósnio",
  bulgarian: "Búlgaro",
  burmese: "Birmanês",
  catalan: "Catalão",
  chineseSimp: "Chinês (simplificado)",
  chineseTrad: "Chinês (tradicional)",
  creole: "Crioulo haitiano",
  croatian: "Croata",
  czech: "Tcheco",
  danish: "Dinamarquês",
  dari: "Dari",
  dutch: "Holandês",
  english: "Inglês",
  estonian: "Estoniano",
  farsi: "Persa (Farsi)",
  finnish: "Finlandês",
  french: "Francês",
  frenchCan: "Francês (canadense)",
  georgian: "Georgiano",
  german: "Alemão",
  greek: "Grego",
  gujrati: "Guzerate",
  hausa: "Hauçá",
  hebrew: "Hebraico",
  hindi: "Hindi",
  hmong: "Hmong",
  hungarian: "Húngaro",
  igbo: "Igbo",
  indonesian: "Indonésio",
  italian: "Italiano",
  japanese: "Japonês",
  kannada: "Canarês",
  khmer: "Khmer",
  korean: "Coreano",
  kurdish: "Curdo",
  lao: "Laosiano",
  latin: "Latim",
  latvian: "Letão",
  lithuanian: "Lituano",
  macedonian: "Macedônio",
  malay: "Malaio",
  malayalam: "Malaiala",
  maltese: "Maltês",
  marathi: "Marati",
  nepali: "Nepalês",
  norwegian: "Norueguês",
  pashto: "Pashto",
  persian: "Persa",
  polish: "Polonês",
  portugueseBrazil: "Português (Brasil)",
  portuguesePortugal: "Português (Portugal)",
  punjabi: "Punjabi",
  romanian: "Romeno",
  russian: "Russo",
  serbian: "Sérvio",
  slovak: "Eslovaco",
  slovenian: "Esloveno",
  somali: "Somali",
  spanish: "Espanhol",
  swahili: "Suaíli",
  swedish: "Sueco",
  tagalog: "Tagalo",
  tamil: "Tâmil",
  telugu: "Telugo",
  thai: "Tailandês",
  tigrinya: "Tigrínia",
  turkish: "Turco",
  ukrainian: "Ucraniano",
  urdu: "Urdu",
  uzbek: "Uzbeque",
  vietnamese: "Vietnamita",
  yoruba: "Iorubá",
};

const LANGUAGE_NAMES = { es: LANGUAGE_NAMES_ES, pt: LANGUAGE_NAMES_PT };

// The dropdown's line under a variant (Data/languages.js, HINTS), in the checkout's languages.
const LANGUAGE_HINTS = {
  es: {
    portuguesePortugal: "También Angola, Mozambique, Cabo Verde",
    chineseSimp: "China continental, Singapur",
    chineseTrad: "Taiwán, Hong Kong, Macao",
  },
  pt: {
    portuguesePortugal: "Também Angola, Moçambique, Cabo Verde",
    chineseSimp: "China continental, Singapura",
    chineseTrad: "Taiwan, Hong Kong, Macau",
  },
};

// The name of a language (by its stored value) in the page's language.
export const languageLabel = (value, lang = currentLang()) => {
  const language = languages.find((l) => l.value === value);
  if (!language) {
    return "";
  }
  return (LANGUAGE_NAMES[lang] && LANGUAGE_NAMES[lang][value]) || language.label;
};

// BCP 47 code of each language, for its name in whatever language the browser translates
// the page into (Intl.DisplayNames). A language without a name there keeps the English one.
const LANGUAGE_CODES = {
  albanian: "sq",
  amharic: "am",
  arabic: "ar",
  armenian: "hy",
  bengali: "bn",
  bosnian: "bs",
  bulgarian: "bg",
  burmese: "my",
  catalan: "ca",
  chineseSimp: "zh-Hans",
  chineseTrad: "zh-Hant",
  creole: "ht",
  croatian: "hr",
  czech: "cs",
  danish: "da",
  dari: "prs",
  dutch: "nl",
  english: "en",
  estonian: "et",
  farsi: "fa",
  finnish: "fi",
  french: "fr",
  frenchCan: "fr-CA",
  georgian: "ka",
  german: "de",
  greek: "el",
  gujrati: "gu",
  hausa: "ha",
  hebrew: "he",
  hindi: "hi",
  hmong: "hmn",
  hungarian: "hu",
  igbo: "ig",
  indonesian: "id",
  italian: "it",
  japanese: "ja",
  kannada: "kn",
  khmer: "km",
  korean: "ko",
  kurdish: "ku",
  lao: "lo",
  latin: "la",
  latvian: "lv",
  lithuanian: "lt",
  macedonian: "mk",
  malay: "ms",
  malayalam: "ml",
  maltese: "mt",
  marathi: "mr",
  nepali: "ne",
  norwegian: "no",
  pashto: "ps",
  persian: "fa",
  polish: "pl",
  portugueseBrazil: "pt-BR",
  portuguesePortugal: "pt-PT",
  punjabi: "pa",
  romanian: "ro",
  russian: "ru",
  serbian: "sr",
  slovak: "sk",
  slovenian: "sl",
  somali: "so",
  spanish: "es",
  swahili: "sw",
  swedish: "sv",
  tagalog: "fil",
  tamil: "ta",
  telugu: "te",
  thai: "th",
  tigrinya: "ti",
  turkish: "tr",
  ukrainian: "uk",
  urdu: "ur",
  uzbek: "uz",
  vietnamese: "vi",
  yoruba: "yo",
};

// "Dari" rather than "Persian (Afghanistan)".
const DIALECT_NAMES = ["prs"];

// Farsi and Persian are one language ("fa"): Farsi is "Persa (Farsi)", next to "Persa".
const NAME_NOTES = { farsi: "Farsi" };

const LANGUAGE_VALUES = new Set(languages.map((l) => l.value));

// Whether a SearchSelect list is (part of) the language list, and not countries or mail options.
export const isLanguageList = (list) =>
  Array.isArray(list) && list.length > 0 && list.every((item) => item && LANGUAGE_VALUES.has(item.value));

// "pt-BR" -> "pt".
const base = (tag) => String(tag || "").toLowerCase().split(/[-_]/)[0];

const capitalize = (text, locale) => {
  const [first = "", ...rest] = Array.from(text);
  return first.toLocaleUpperCase(locale) + rest.join("");
};

// Every language's name in one language, worked out once per language and kept:
// { label, search, lang } by value, and the alphabetical position of each in that language.
// English: our own names. The checkout's Spanish and Brazilian Portuguese: its names above.
// Any other: the browser's (Intl), or nothing when the browser has no names in that language.
const namesByLocale = new Map();

const buildNames = (locale) => {
  const key = base(locale);
  let own = null;
  let display = null;
  let dialect = null;
  if (key === "en") {
    own = {};
  } else if (key === "es") {
    own = LANGUAGE_NAMES_ES;
  } else if (locale === intlLocale("pt")) {
    own = LANGUAGE_NAMES_PT;
  } else {
    try {
      const options = { type: "language", fallback: "none" };
      display = new Intl.DisplayNames([locale], { ...options, languageDisplay: "standard" });
      if (base(display.resolvedOptions().locale) !== key) {
        return null;
      }
      dialect = new Intl.DisplayNames([locale], { ...options, languageDisplay: "dialect" });
    } catch (e) {
      return null;
    }
  }

  const nameOf = (language) => {
    if (own) {
      return own[language.value] ? { label: own[language.value], lang: locale } : null;
    }
    const code = LANGUAGE_CODES[language.value];
    if (!code) {
      return null;
    }
    try {
      const name = (DIALECT_NAMES.includes(code) ? dialect : display).of(code);
      if (!name || name.toLowerCase() === code.toLowerCase()) {
        return null;
      }
      const note = NAME_NOTES[language.value];
      return { label: note ? `${capitalize(name, locale)} (${note})` : capitalize(name, locale), lang: locale };
    } catch (e) {
      return null;
    }
  };

  const entries = languages.map((language) => ({ language, ...(nameOf(language) || { label: language.label, lang: "en" }) }));

  // Two languages with the same name there ("Persian" twice) say which is which.
  const seen = {};
  entries.forEach((entry) => {
    const k = entry.label.toLocaleLowerCase(locale);
    seen[k] = (seen[k] || 0) + 1;
  });
  entries.forEach((entry) => {
    if (seen[entry.label.toLocaleLowerCase(locale)] > 1 && entry.label !== entry.language.label) {
      entry.label = `${entry.label} (${entry.language.label})`;
    }
  });

  let compare;
  try {
    compare = new Intl.Collator(locale, { sensitivity: "base" }).compare;
  } catch (e) {
    compare = (a, b) => a.localeCompare(b);
  }
  const sorted = entries.slice().sort((a, b) => compare(a.label, b.label) || compare(a.language.label, b.language.label));

  const byValue = new Map();
  sorted.forEach((entry, rank) => {
    const spanish = LANGUAGE_NAMES_ES[entry.language.value] || "";
    const portuguese = LANGUAGE_NAMES_PT[entry.language.value] || "";
    byValue.set(entry.language.value, {
      label: entry.label,
      lang: entry.lang,
      rank,
      // Found by the name shown, the English one, the Spanish and Portuguese ones, its countries
      // (Data/languages.js, ALIASES), with or without accents.
      search: searchable(`${entry.label} ${entry.language.label} ${spanish} ${portuguese} ${entry.language.value} ${(entry.language.aliases || []).join(" ")}`),
    });
  });
  return byValue;
};

const namesIn = (locale) => {
  if (!namesByLocale.has(locale)) {
    namesByLocale.set(locale, buildNames(locale));
  }
  return namesByLocale.get(locale);
};

// Pinned languages carry how often the account ordered them ("3 orders"): the word
// is the same in Spanish and Portuguese.
const translatedNote = (note) => {
  const count = typeof note === "string" && note.match(/^(\d+) orders?$/);
  return count ? `${count[1]} ${count[1] === "1" ? "pedido" : "pedidos"}` : note;
};

// The language list (or pinned shortcuts) as the dropdown shows it. Values never change
// (they are what the order and the quote store); only labels and order do:
// - English page: the list as it is.
// - Spanish or Portuguese checkout: names in that language, in its alphabetical order, found
//   by that name or the English one, with or without accents.
// - Page translated by the browser (translatedTo, from useBrowserTranslation): names in that
//   language, in its alphabetical order, flagged `frozen` so the translator leaves them be.
// Pinned shortcuts ({ sort: false }) keep their own order.
export const languageOptions = (list, lang = currentLang(), { sort = true, translatedTo = null } = {}) => {
  const locale = translatedTo || (isTranslated(lang) ? intlLocale(lang) : null);
  const names = locale && namesIn(locale);
  if (!names) {
    return list;
  }
  const out = list.map((language) => {
    const name = names.get(language.value);
    if (!name) {
      return language;
    }
    return {
      ...language,
      label: name.label,
      note: isTranslated(lang) ? translatedNote(language.note) : language.note,
      hint: (LANGUAGE_HINTS[lang] && LANGUAGE_HINTS[lang][language.value]) || language.hint,
      search: name.search,
      ...(translatedTo ? { frozen: true, labelLang: name.lang } : {}),
    };
  });
  if (!sort) {
    return out;
  }
  const rank = (language) => {
    const name = names.get(language.value);
    return name ? name.rank : Number.MAX_SAFE_INTEGER;
  };
  return out.sort((a, b) => rank(a) - rank(b));
};

// Countries for the mailing address, from country-state-city ({label: English
// name, value: ISO code}). The address keeps the English name; another language
// shows its own (from the browser) and finds either.
const regionNames = {};
const regionName = (code, fallback, locale) => {
  try {
    if (!regionNames[locale]) {
      regionNames[locale] = new Intl.DisplayNames([locale], { type: "region" });
    }
    const name = regionNames[locale].of(code);
    return name && name !== code ? name : fallback;
  } catch (e) {
    return fallback;
  }
};

export const countryOptions = (countries, lang = currentLang()) => {
  if (!isTranslated(lang)) {
    return countries;
  }
  const locale = intlLocale(lang);
  return countries
    .map((country) => {
      const label = regionName(country.value, country.label, locale);
      return { ...country, label, search: searchable(`${label} ${country.label}`) };
    })
    .sort((a, b) => a.label.localeCompare(b.label, locale));
};

// The mailing options ("USPS Priority: 2-3 days - $19.95") in the page's language.
export const deliveryMethodOptions = (list, lang = currentLang()) => {
  if (!isTranslated(lang)) {
    return list;
  }
  const days = lang === "pt" ? "dias" : "días";
  return list.map((option) => ({
    ...option,
    label: option.label.replace(/(\d+-\d+) days/, `$1 ${days}`).replace(/\$/g, "US$"),
  }));
};

// "How did you hear about us?": the value is what office sees on the order.
const HOW_DID_YOU_HEAR = {
  es: {
    google: { label: "Búsqueda en Google" },
    "google maps": { label: "Google Maps" },
    "ai assistant": { label: "ChatGPT u otro asistente de IA", extraInputLabel: "¿Cuál?" },
    bing: { label: "Bing u otro buscador" },
    "social media": { label: "Redes sociales" },
    ads: { label: "Un anuncio" },
    article: { label: "Un artículo o blog" },
    "referred by a friend/colleague": { label: "Me lo recomendó un amigo o colega", extraInputLabel: "¿Quién se lo recomendó?" },
    "recommended by my attorney/law office": { label: "Me lo recomendó mi abogado o bufete", extraInputLabel: "Abogado o bufete" },
    other: { label: "Otro", extraInputLabel: "Cuéntenos dónde" },
  },
  pt: {
    google: { label: "Busca no Google" },
    "google maps": { label: "Google Maps" },
    "ai assistant": { label: "ChatGPT ou outro assistente de IA", extraInputLabel: "Qual?" },
    bing: { label: "Bing ou outro buscador" },
    "social media": { label: "Redes sociais" },
    ads: { label: "Um anúncio" },
    article: { label: "Um artigo ou blog" },
    "referred by a friend/colleague": { label: "Indicação de um amigo ou colega", extraInputLabel: "Quem indicou?" },
    "recommended by my attorney/law office": { label: "Indicação do meu advogado ou escritório", extraInputLabel: "Advogado ou escritório" },
    other: { label: "Outro", extraInputLabel: "Conte onde" },
  },
};

// The list some options ask from (which AI assistant): the value is saved as
// it is in English, only the label changes.
const HEAR_CHOICES = {
  es: {
    "Google Gemini or AI Overview": "Google Gemini o la respuesta de IA de Google",
    "Another one": "Otro",
  },
  pt: {
    "Google Gemini or AI Overview": "Google Gemini ou a resposta de IA do Google",
    "Another one": "Outro",
  },
};

export const howDidYouHearOptions = (options, lang = currentLang()) => {
  const choices = (option) => (option.extraChoices
    ? { extraChoices: option.extraChoices.map((c) => ({ value: c, label: (HEAR_CHOICES[lang] && HEAR_CHOICES[lang][c]) || c })) }
    : {});
  const own = HOW_DID_YOU_HEAR[lang];
  if (!own) {
    return options.map((option) => ({ ...option, ...choices(option) }));
  }
  return options.map((option) => ({ ...option, ...(own[option.value] || {}), ...choices(option) }));
};
